import { useEffect, useLayoutEffect, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { HeroPointChip } from './HeroPointChip';
import { useAtmosphereSettings } from '../../hooks/useAtmosphereSettings';

/**
 * A6 — Portal-based chip dispatch animation.
 *
 * The chip flies from the PC portrait (source) to the roll result card
 * (target). Both elements may live in different ResizablePanels so
 * `layoutId` won't reach across — we render the animating chip into
 * `document.body` via createPortal and animate via absolute positioning
 * using bounding-box math from getBoundingClientRect.
 *
 * Source / target refs are typed `RefObject<HTMLElement | null>` so the
 * portal can no-op cleanly when either is unmounted. If the bounding-
 * box can't be resolved (null ref OR zero-sized rect), the chip
 * animates between viewport-center fallback positions so the cinematic
 * still resolves and `onAnimationComplete` still fires.
 *
 * Lifecycle:
 *   - On mount: compute source + target screen positions (memoized for
 *     the lifetime of the mount — the chip flies once, doesn't track).
 *   - framer-motion animates between them.
 *   - `onAnimationComplete` fires when the chip lands at the target.
 *   - Parent (ScenarioRunner) then proceeds to the QR upgrade flow.
 *
 * Audio: A6 fires no SFX directly. The QR upgrade flow that follows
 * the chip animation will re-fire QRCinematicResponse, which will
 * play the upgraded QR's brass-swell. That's the audio answer to the
 * spend gesture.
 */

export interface HeroPointChipPortalProps {
  /** PC portrait element to fly from. */
  sourceRef: RefObject<HTMLElement | null>;
  /** Roll result card element to fly to. */
  targetRef: RefObject<HTMLElement | null>;
  /** Fired once the chip has landed at the target. */
  onAnimationComplete: () => void;
  /** Flight duration in ms. Default 700ms — snappier than the QR tumble. */
  durationMs?: number;
  /** Optional testid prefix; default 'br-hero-point-chip-portal'. */
  testidPrefix?: string;
}

const DEFAULT_DURATION_MS = 700;

interface ScreenPoint {
  x: number;
  y: number;
}

function centerOfRef(ref: RefObject<HTMLElement | null>): ScreenPoint | null {
  const el = ref.current;
  if (!el) {
    return null;
  }
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 && rect.height <= 0) {
    return null;
  }
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
}

function viewportCenter(): ScreenPoint {
  const w = typeof window === 'undefined' ? 1024 : window.innerWidth;
  const h = typeof window === 'undefined' ? 768 : window.innerHeight;
  return { x: w / 2, y: h / 2 };
}

export function HeroPointChipPortal({
  sourceRef,
  targetRef,
  onAnimationComplete,
  durationMs = DEFAULT_DURATION_MS,
  testidPrefix = 'br-hero-point-chip-portal',
}: HeroPointChipPortalProps) {
  // A9 retrofit: gate the chip animation on the atmosphere settings.
  // When disabled, fire onAnimationComplete immediately so the parent's
  // QR-upgrade flow continues without the visual dispatch.
  const { heroPointChipEnabled, animationsEnabled } = useAtmosphereSettings();
  const animationActive = heroPointChipEnabled && animationsEnabled;

  useEffect(() => {
    if (animationActive) return;
    onAnimationComplete();
  }, [animationActive, onAnimationComplete]);

  // Positions are resolved AFTER the parent's refs commit. The first
  // render returns null; useLayoutEffect synchronously computes the
  // source + target centers and triggers a second render with the
  // real positions. The chip animation starts from the second render,
  // which is the first render the user sees painted on screen.
  const [positions, setPositions] = useState<{
    source: ScreenPoint;
    target: ScreenPoint;
  } | null>(null);

  useLayoutEffect(() => {
    const fallback = viewportCenter();
    setPositions({
      source: centerOfRef(sourceRef) ?? fallback,
      target: centerOfRef(targetRef) ?? fallback,
    });
  }, [sourceRef, targetRef]);

  const durationSeconds = durationMs / 1000;

  if (!animationActive) {
    return null;
  }

  if (typeof document === 'undefined') {
    // SSR / no-DOM environment — render nothing; the parent should
    // never mount the portal in this case, but defend defensively.
    return null;
  }

  if (positions === null) {
    return null;
  }

  const { source, target } = positions;

  return createPortal(
    <div
      className="br-hero-point-chip-portal-root"
      data-testid={`${testidPrefix}-root`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        pointerEvents: 'none',
      }}
    >
      <motion.div
        data-testid={testidPrefix}
        style={{ position: 'absolute', willChange: 'transform' }}
        initial={{
          x: source.x,
          y: source.y,
          scale: 0.7,
          opacity: 0,
        }}
        animate={{
          x: [source.x, source.x, target.x],
          y: [source.y, source.y, target.y],
          scale: [0.7, 1.3, 1.0],
          opacity: [0, 1, 1],
        }}
        transition={{
          duration: durationSeconds,
          times: [0, 0.4, 1],
          ease: 'easeOut',
        }}
        onAnimationComplete={onAnimationComplete}
      >
        {/* The chip element itself is decorative (aria-hidden). The
         *   portal-root wrapper is also pointer-events:none so the chip
         *   never intercepts clicks during its flight. */}
        <div style={{ transform: 'translate(-50%, -50%)' }}>
          <HeroPointChip variant="large" testid={`${testidPrefix}-chip`} />
        </div>
      </motion.div>
    </div>,
    document.body,
  );
}

export default HeroPointChipPortal;
